---
title: "Funding/Overnight Fees, Swaps and Trading Costs"
slug: trading-costs-swaps-fees
shortTitle: "Swaps, Fees & Costs"
description: "What a trade really costs: the spread, the trade fee, the daily swap and slippage, how swaps differ from crypto funding rates, and a worked cost example in USDT."
level: intermediate
category: trading
course: intermediate
order: 15
topics: [cfds, risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/trading-costs-swaps-fees.svg
faqs:
  - q: "Does {{BRAND}} charge crypto funding rates?"
    a: "No. Funding rates belong to perpetual futures on crypto exchanges. Every market on {{BRAND}} is a CFD, which uses a daily swap instead, set per market and charged once a day."
  - q: "Why was I charged three days of swap at once?"
    a: "Markets other than crypto do not charge swap at weekends. To cover those two days, one weekday carries a triple charge, shown as the triple-swap day in the market details."
  - q: "Can a swap be paid to me instead of charged?"
    a: "Yes. Each market has separate long and short swap rates. A negative rate is charged to your account and a positive rate is credited to it."
  - q: "Does leverage increase my trading costs?"
    a: "The spread, fee and swap are all worked out on the full position value, not on your margin. Leverage itself does not change the rate, but it lets you open a larger position with the same money, and larger positions cost more."
  - q: "How can I avoid paying swap?"
    a: "Swap only applies to positions still open at the daily swap time, so trades opened and closed within the same day before that time pay none. The spread and the trade fee still apply."
---

Every trade has a price tag, and it is easy to miss because it is not shown as one number. On {{BRAND}} there are three explicit costs: the **spread** built into the buy and sell prices, the **trade fee**, and the **swap**, a daily charge or credit for holding a position overnight. On top of those sits **slippage**, the gap between the price you expected and the price you got.

Each cost is small on a single trade. Together, and repeated over many trades or many days, they can decide whether a strategy makes or loses money. This lesson explains each one, how a CFD swap differs from the funding rates you may know from crypto exchanges, and how to add them up in USDT.

<Warning title="Costs are charged on the full position">

The spread, the fee and the swap are all worked out on the position's full value, not on the margin you put down. With leverage, a position can cost far more to hold than the margin suggests, and costs accumulate whether the trade is winning or losing.

</Warning>

<CTA />

<KeyTakeaways>

- **Three costs, three moments.** The spread is paid as you open, the fee is shown before you confirm, and the swap is applied each day you hold past the swap time.
- **Costs scale with position value.** Leverage does not shrink them; a 6,000 USDT position costs the same whether its margin is 600 or 1,200 USDT.
- **A swap replaces funding.** Crypto exchanges charge funding on perpetual futures; CFDs on {{BRAND}} use a daily swap instead.
- **Holding time changes the math.** A cost that barely matters on a one-hour trade can take a large share of a gain on a position held for weeks.

</KeyTakeaways>

## The spread

Every market shows two prices. You buy at the **Ask** and sell at the **Bid**, and the gap between them is the **spread**. Because you open at one side and close at the other, a new position starts slightly in the red. The cost is simply the spread times your size: a 30 USDT spread on 0.1 BTC is 0.1 × 30 = 3 USDT.

Spreads are not fixed. They tend to widen when markets are thin, around the open of a session and around major news. [Bid, Ask, Spread and Why They Matter](/academy/article/bid-ask-spread) covers this in depth.

## The trade fee

The trade fee is {{TRADE_FEE}}, on the line labeled **Fee** in the order summary. After the trade, it appears in the **Fees** column of Closed Positions. Depending on the market, the fee may be a percentage of the position value charged when you open and again when you close, or a flat amount per trade. Each market's page under Markets explains which applies.

{/* TODO: verify with platform — current fee structure per market (percentage on open and close vs flat per trade). */}

## The swap: the overnight cost

A **swap** is a daily adjustment for keeping a position open past {{SWAP_HOUR}}. It reflects the cost of financing a leveraged position, and it is calculated as:

**Swap = position value × the market's long or short swap rate**

Buy and sell positions have separate rates. A negative rate is charged to you; a positive rate is credited. A market's page shows **Swap long / short** as a percentage per day, and when it is charged.

<Screenshot name="market-fee-and-swap" alt="A market's page listing its lot size, leverage range, fee, swap long and short rates, swap timing and trading hours" caption="Each market's page shows its fee, its long and short swap rates and when swap is charged." />

 Markets other than crypto are charged on weekdays only, with three days' worth on {{SWAP_TRIPLE_DAY}} to cover the weekend. Crypto markets are charged every day. Swap counts in your equity while the position is open, and shows in the **Swap** column of Closed Positions afterwards.

{/* TODO: verify with platform — current crypto swap rates (they may currently be zero) and which markets carry a swap. */}

## Funding rates, and why you won't pay them here

Many crypto exchanges offer **perpetual futures**, futures contracts with no expiry date. To keep their price close to the spot price, they use a **funding rate**: at fixed intervals, often every eight hours, traders on one side pay traders on the other. When funding is positive, longs pay shorts; when negative, shorts pay longs. The rate changes with how traders are positioned.

{{BRAND}} does not offer perpetual futures, so there is no funding rate on your positions. Every market is a CFD with a daily swap instead. Funding rates are still worth knowing about as outside market data, because they hint at how crowded one side of the crypto market is. [Market Sentiment](/academy/article/market-sentiment) explains how traders read them.

<CompareTable caption="Funding rates compared with swaps">

| | Funding rate | Swap on {{BRAND}} |
| --- | --- | --- |
| Where it applies | Perpetual futures on crypto exchanges | CFDs on every market |
| How often | Fixed intervals, often several times a day | Once a day |
| Who receives it | Traders on the other side | Charged to or credited to your account |
| How the rate is set | Moves with market positioning | Set per market, separately for long and short |
| Weekends | Charged as normal | Crypto every day; other markets triple on one weekday |

</CompareTable>

## Slippage: the cost nobody quotes

**Slippage** is the difference between the price you expected and the price you were filled at. Market orders and triggered stop losses fill at the next available price, so in a fast market or a gap they can fill worse than expected. It is not a fee, but it is a real cost. Limit orders avoid it by filling only at your price or better, at the risk of not filling at all.

## Adding it all up

<Example title="A Bitcoin trade held for five days (illustrative prices and rates)">

You buy 0.1 BTC/USDT at 60,000, a position worth 0.1 × 60,000 = **6,000 USDT**. Say the spread is 30 USDT, the fee is 0.05% on open and on close, and the long swap rate is −0.03% a day. These are hypothetical numbers, not the platform's rates.

- Spread: 0.1 × 30 = **3 USDT**.
- Fee: 6,000 × 0.0005 = 3 USDT each way, about **6 USDT** in total.
- Swap: 6,000 × 0.0003 = 1.80 USDT a day; crypto is charged daily, so 5 × 1.80 = **9 USDT**.
- Total cost: 3 + 6 + 9 = **18 USDT**.
- If BTC rises to 61,000, the gross gain is (61,000 − 60,000) × 0.1 = 100 USDT, and the net is about 100 − 18 = **82 USDT**.

</Example>

Costs took nearly a fifth of that gain. Held for 30 days instead of 5, the swap alone would be 30 × 1.80 = 54 USDT. For markets other than crypto, a full week always includes one triple day: a gold position charged 2.40 USDT a day pays five weekday charges, one of them tripled, so 7 × 2.40 = 16.80 USDT for the week.

<ProTip>

Before you hold any position overnight, work out its daily swap: position value × the swap rate on the market's page. If a week of swap would eat most of your target, the trade suits a shorter holding period. [Scalping vs Day Trading vs Swing Trading](/academy/article/scalping-day-swing-trading) looks at how holding time shapes a strategy.

</ProTip>

## Check your costs before and after

<Steps>
<Step title="Before: read the prices and the summary">

Look at the Bid and Ask on the trade screen, then read **Fee** and **Margin required** in the order summary.

</Step>
<Step title="Before: check the swap">

On the market's page, note **Swap long / short** and when swap is charged if you might hold overnight.

</Step>
<Step title="After: compare gross and net">

In **Closed Positions**, compare **Fees**, **Swap** and **Net PnL** with the price move. Record them in your journal.

</Step>
</Steps>

## Conclusion

Trading costs come in four forms: the spread you cross to open, the trade fee, the daily swap and slippage in fast markets. All are worked out on the full position value, so leverage does not shrink them. Crypto exchanges' funding rates do not apply on {{BRAND}}, where a daily swap takes their place. Check the costs before each trade, keep an eye on holding time, and compare gross and net results afterwards. Many traders who look profitable before costs are not after them.
